var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, withRouter } from 'react-router-dom';
import { formatMoney, formatNumber } from '../../common/helpers';
import { useUrlProps } from '../../common/urlProps';
import useFetch from '../../common/useFetch';
import useInterval from '../../common/useInterval';
import useOnClickOutside from '../../common/useOnClickOutside';
import DrawService from '../../domain/DrawService';
import WonPrizeService from '../../domain/WonPrizeService';
import gs from '../../styles/style';
import { UserContext } from '../Auth/UserProvider';
import Breadcrumbs from '../Common/Breadcrumbs';
import { Pager, SearchInput, TableLoader, useDataframe } from '../Common/Dataframe';
import DateTime from '../Common/DateTime';
import Duration from '../Common/Duration';
import ListLimiter from '../Common/ListLimiter';
import Loader from '../Common/Loader';
import { ConfirmationModal, useModal } from '../Common/Modal';
import TicketNumber from '../Tickets/TicketNumber';
import DrawResult from './DrawResult';
var FontAwesomeIcon = require('@fortawesome/react-fontawesome').FontAwesomeIcon;
var Draw = function (_a) {
    var history = _a.history, match = _a.match;
    var t = useTranslation(['general', 'draw_statuses'])[0];
    var user = useContext(UserContext).user;
    var _b = useFetch(function (config) { return DrawService.getDraw(match.params.draw, config); }), draw = _b.data, getDraw = _b.get, setDraw = _b.setData, loading = _b.loading;
    var deleteDraw = useFetch(DrawService.deleteDraw, {
        onSuccess: function () {
            history.push('/draws');
        }
    }).post;
    var deleteModal = useModal({
        onClose: function (confirm) {
            if (confirm) {
                deleteDraw(draw);
            }
        }
    });
    useEffect(function () {
        getDraw();
    }, []);
    useInterval(function () {
        getDraw();
    }, draw && draw.executing ? 3000 : null);
    if (!draw) {
        return React.createElement(Loader, null);
    }
    var showTicketExport = (user.isNotary() || user.isManager()) && draw.is_synced;
    return React.createElement("div", null,
        React.createElement(Breadcrumbs, null,
            React.createElement(Link, { to: "/draws" }, t('draws'))),
        React.createElement("div", { className: gs('section-header') },
            React.createElement("h1", { className: gs('section-title') }, draw.title),
            user.isManager() && React.createElement("div", { className: gs('section-options') },
                draw.editable && React.createElement(Link, { to: "/draws/".concat(draw.id, "/edit"), className: gs('btn', 'btn-outline-secondary', { disabled: loading }) }, t('edit')),
                ' ',
                draw.deletable && React.createElement("button", { type: "button", onClick: function () { return deleteModal.open(); }, className: gs('btn', 'btn-outline-danger') }, t('delete')))),
        React.createElement("div", { className: gs('row', 'mb-4') },
            React.createElement("div", { className: gs('col-md-3') },
                React.createElement("h2", null, formatNumber(draw.customers_count)),
                t('customers')),
            React.createElement("div", { className: gs('col-md-3') },
                React.createElement("h2", null, formatNumber(draw.participations_count)),
                !showTicketExport && t('tickets'),
                showTicketExport && (React.createElement("a", { href: '/api/draws/' + draw.id + '/export_tickets', download: true },
                    React.createElement(FontAwesomeIcon, { icon: "download" }),
                    ' ',
                    t('tickets')))),
            React.createElement("div", { className: gs('col-md-3') },
                React.createElement("h2", null, formatMoney(draw.revenue)),
                t('revenue'))),
        React.createElement("div", { className: gs('card-group', 'mb-4') },
            React.createElement("div", { className: gs('card') },
                React.createElement("div", { className: gs('card-body') },
                    React.createElement("table", { className: gs('table', 'table-borderless', 'table-sm', 'mb-0') },
                        React.createElement("tbody", null,
                            React.createElement("tr", null,
                                React.createElement("td", { className: gs('w-25') },
                                    React.createElement("strong", null, t('draw_at'))),
                                React.createElement("td", null,
                                    React.createElement(DateTime, { date: draw.draw_at }))),
                            React.createElement("tr", null,
                                React.createElement("td", null,
                                    React.createElement("strong", null, t('cutoff_at'))),
                                React.createElement("td", null,
                                    React.createElement(DateTime, { date: draw.cutoff_at }),
                                    ' ',
                                    React.createElement("span", { className: gs('text-muted') },
                                        "(\u2212 ",
                                        React.createElement(Duration, { start: draw.cutoff_at, end: draw.draw_at }),
                                        ")"))),
                            React.createElement("tr", null,
                                React.createElement("td", null,
                                    React.createElement("strong", null, t('status'))),
                                React.createElement("td", null,
                                    t("draw_statuses:".concat(draw.status)),
                                    draw.verified_at && React.createElement(React.Fragment, null,
                                        ' ',
                                        " (",
                                        React.createElement(DateTime, { date: draw.verified_at }),
                                        ")"))),
                            showTicketExport && (React.createElement("tr", null,
                                React.createElement("td", null,
                                    React.createElement("strong", null, t('password'))),
                                React.createElement("td", null, draw.password))))))),
            React.createElement("div", { className: gs('card') },
                React.createElement("div", { className: gs('card-body') },
                    React.createElement("table", { className: gs('table', 'table-borderless', 'table-sm', 'mb-0') },
                        React.createElement("tbody", null,
                            React.createElement("tr", null,
                                React.createElement("td", { className: gs('w-25') },
                                    React.createElement("strong", null, t('draw_category'))),
                                React.createElement("td", null, draw.draw_category.title)),
                            React.createElement("tr", null,
                                React.createElement("td", { className: gs('w-25') },
                                    React.createElement("strong", null, t('ticket_types'))),
                                React.createElement("td", null, draw.draw_category.ticket_types.map(function (_a) {
                                    var title = _a.title;
                                    return title;
                                }).join(', '))),
                            React.createElement("tr", null,
                                React.createElement("td", null,
                                    React.createElement("strong", null, t('prize_scheme'))),
                                React.createElement("td", null,
                                    React.createElement(Link, { to: "/prize-schemes/".concat(draw.prize_scheme.id) }, draw.prize_scheme.title)))))))),
        user.isNotary() && (draw.executable || draw.executing) && React.createElement("div", { className: gs('mb-4') },
            React.createElement("hr", { className: gs('mt-4', 'mb-4') }),
            React.createElement(ExecuteDrawSection, { draw: draw, setDraw: setDraw })),
        draw.draw_results.length > 0 && React.createElement("div", { className: gs('mb-4') },
            React.createElement("hr", { className: gs('mt-4', 'mb-4') }),
            React.createElement(DrawResultsSection, { draw: draw, setDraw: setDraw })),
        React.createElement(ConfirmationModal, { modal: deleteModal },
            React.createElement("h5", { className: gs('my-2') }, t('confirm_delete', { title: draw.title }))));
};
var ExecuteDrawSection = function (_a) {
    var draw = _a.draw, setDraw = _a.setDraw;
    var t = useTranslation()[0];
    var urlProps = useUrlProps('result');
    var _b = useFetch(function (config) { return DrawService.executeDraw(draw, config); }, {
        onSuccess: function (draw) {
            urlProps.clear();
            setDraw(draw);
        }
    }), executeDraw = _b.post, loading = _b.loading;
    var executeModal = useModal({
        onClose: function (confirm) {
            if (confirm) {
                executeDraw();
            }
        }
    });
    var executing = draw.executing || loading;
    return (React.createElement(React.Fragment, null,
        React.createElement("h3", { className: gs('mb-4') }, t('execute_draw')),
        React.createElement("div", { className: gs('card') },
            React.createElement("div", { className: gs('card-body') },
                React.createElement("div", { className: gs('row') },
                    React.createElement("div", { className: gs('col-md-8'), dangerouslySetInnerHTML: {
                            __html: t('execute_draw_description', {
                                draw_mechanism: "".concat(draw.prize_scheme.draw_mechanism.title),
                                prize_scheme: "".concat(draw.prize_scheme.title),
                                tickets: "".concat(t('tickets_count', { count: draw.participations_count, formatted_count: formatNumber(draw.participations_count) })),
                                interpolation: { escapeValue: true }
                            })
                        } }),
                    React.createElement("div", { className: gs('col-md-4', 'text-right') },
                        executing && React.createElement("span", { className: gs('mr-3') },
                            React.createElement(Loader, null)),
                        React.createElement("button", { type: "button", onClick: function () { return executeModal.open(); }, disabled: executing, className: gs('btn', 'btn-primary') }, t('execute')))))),
        React.createElement(ConfirmationModal, { modal: executeModal },
            React.createElement("h5", { className: gs('my-2') }, t('confirm_execute_draw')))));
};
var DrawResultsSection = function (_a) {
    var draw = _a.draw, setDraw = _a.setDraw;
    var t = useTranslation()[0];
    var user = useContext(UserContext).user;
    var urlProps = useUrlProps('result');
    var _b = useFetch(function (config) { return DrawService.verifyDraw(draw, config); }, {
        onSuccess: function (draw) {
            urlProps.clear();
            setDraw(draw);
        }
    }), verifyDraw = _b.post, verifying = _b.loading;
    var _c = useFetch(function (config) { return DrawService.rejectDraw(draw, config); }, {
        onSuccess: function (draw) {
            urlProps.clear();
            setDraw(draw);
        }
    }), rejectDraw = _c.post, rejecting = _c.loading;
    var _d = useState(false), showDrawResultDropdown = _d[0], setShowDrawResultDropdown = _d[1];
    var drawResultsDropdownRef = useRef();
    useOnClickOutside(drawResultsDropdownRef, function () {
        setShowDrawResultDropdown(false);
    });
    var verifyModal = useModal({
        onClose: function (confirm) {
            if (confirm) {
                verifyDraw();
            }
        }
    });
    var rejectModal = useModal({
        onClose: function (confirm) {
            if (confirm) {
                rejectDraw();
            }
        }
    });
    var getActiveDrawResultIndex = function () { return Math.min(draw.draw_results.length - 1, Math.max(0, parseInt(urlProps.get('draw_result')) || 0)); };
    var _e = useState(getActiveDrawResultIndex()), activeDrawResultIndex = _e[0], setActiveDrawResultIndex = _e[1];
    var activeDrawResult = draw.draw_results[activeDrawResultIndex];
    useEffect(function () {
        setActiveDrawResultIndex(getActiveDrawResultIndex());
    }, [getActiveDrawResultIndex()]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: gs('clearfix', 'mb-4') },
            React.createElement("h3", { className: gs('my-1', 'float-left') }, t('result')),
            React.createElement("div", { className: gs('float-right') },
                draw.draw_results.length > 1 && React.createElement("div", { className: gs('btn-group') },
                    React.createElement("button", { type: "button", className: gs('btn', 'btn-outline-secondary', 'btn-sm', 'dropdown-toggle'), onClick: function () { return setShowDrawResultDropdown(true); } }, t('attempts_count', { count: draw.draw_results.length })),
                    React.createElement("div", { className: gs('dropdown-menu', 'dropdown-menu-right', { show: showDrawResultDropdown }), ref: drawResultsDropdownRef }, draw.draw_results.map(function (drawResult, index) { return (React.createElement(Link, { to: urlProps.replaceLink({ draw_result: index === 0 ? null : index }), onClick: function () { return setShowDrawResultDropdown(false); }, className: gs('dropdown-item', 'link', { 'active': drawResult.id == activeDrawResult.id }), key: drawResult.id },
                        React.createElement(DateTime, { date: drawResult.executed_at }),
                        ' ',
                        React.createElement("small", { className: gs('text-muted') },
                            "(#",
                            draw.draw_results.length - index,
                            ")"))); }))),
                ' ',
                !activeDrawResult.rejected_at && React.createElement("a", { href: '/api/draw_results/' + activeDrawResult.id + '/export', className: gs('btn', 'btn-secondary', 'btn-sm'), download: true },
                    React.createElement(FontAwesomeIcon, { icon: "download" }),
                    ' ',
                    t('export')))),
        React.createElement("div", { className: gs('card', 'mb-4') },
            React.createElement("div", { className: gs('card-body') },
                React.createElement("div", { className: gs('row') },
                    React.createElement("div", { className: gs('col-md-6') },
                        activeDrawResult.result && (React.createElement("h4", { className: gs('mb-2', { 'text-success': draw.verified_at && !activeDrawResult.rejected_at, 'text-danger': activeDrawResult.rejected_at }) },
                            React.createElement(DrawResult, { result: activeDrawResult.result }))),
                        draw.draw_results.length > 1 && React.createElement("small", { className: gs('mr-3', 'text-muted') },
                            "#",
                            activeDrawResult.attempt),
                        React.createElement("small", null,
                            React.createElement("strong", null,
                                t('executed_at'),
                                ":"),
                            ' ',
                            React.createElement(DateTime, { date: activeDrawResult.executed_at })),
                        React.createElement("br", null),
                        activeDrawResult.rejected_at && (React.createElement(React.Fragment, null,
                            React.createElement("small", null,
                                React.createElement("strong", null,
                                    t('rejected_at'),
                                    ":"),
                                ' ',
                                React.createElement(DateTime, { date: activeDrawResult.rejected_at })),
                            React.createElement("br", null))),
                        React.createElement("small", null,
                            React.createElement("strong", null,
                                t('rng_run'),
                                ":"),
                            ' ',
                            React.createElement(ListLimiter, { limit: 1, items: activeDrawResult.rng_reports.map(function (report) { return React.createElement("a", { href: "https://lotus-rng.emolifeconnect.com/?id=".concat(report.id), target: "_blank", rel: "noopener noreferrer", key: report.id },
                                    React.createElement("code", null, report.id),
                                    ' (',
                                    React.createElement("code", null, report.hash_sha1),
                                    ')'); }) })),
                        React.createElement("br", null),
                        React.createElement("small", null,
                            React.createElement("strong", null,
                                t('rng_version'),
                                ":"),
                            ' ',
                            React.createElement("code", null, activeDrawResult.system_version))),
                    React.createElement("div", { className: gs('col-md-6') },
                        React.createElement("div", { className: gs('row') },
                            React.createElement("div", { className: gs('col-md-3') },
                                React.createElement("h4", null, formatNumber(activeDrawResult.participations_count)),
                                t('tickets')),
                            React.createElement("div", { className: gs('col-md-3') },
                                React.createElement("h4", null, formatNumber(activeDrawResult.won_prizes_count)),
                                t('won_prizes')),
                            React.createElement("div", { className: gs('col-md-3') },
                                React.createElement("h4", null, formatMoney(activeDrawResult.value)),
                                t('total_winnings')),
                            React.createElement("div", { className: gs('col-md-3') },
                                React.createElement("h4", null, formatMoney(activeDrawResult.tax_amount)),
                                t('tax_amount'))))),
                !activeDrawResult.rejected_at && (draw.verifiable || draw.rejectable) && React.createElement("div", null,
                    React.createElement("hr", { className: gs('my-3') }),
                    React.createElement("div", { className: gs('row') },
                        React.createElement("div", { className: gs('col-md-8') },
                            React.createElement("strong", null, t('draw_result_awaiting_verification')),
                            ' ',
                            t('draw_result_awaiting_verification_description')),
                        React.createElement("div", { className: gs('col-md-4', 'text-right') },
                            (verifying || rejecting) && React.createElement("span", { className: gs('mr-3') },
                                React.createElement(Loader, null)),
                            user.isNotary() && draw.verifiable && React.createElement("button", { type: "button", onClick: function () { return verifyModal.open(); }, disabled: verifying, className: gs('btn', 'btn-primary') }, t('verify')),
                            ' ',
                            user.isManager() && draw.rejectable && React.createElement("button", { type: "button", onClick: function () { return rejectModal.open(); }, disabled: rejecting, className: gs('btn', 'btn-danger') }, t('reject'))))))),
        React.createElement("ul", { className: gs('nav', 'nav-tabs', 'mb-4') },
            React.createElement("li", { className: gs('nav-item') },
                React.createElement(Link, { to: urlProps.mergeLink({ tab: null, won_prizes: null }), className: gs('nav-link', { active: !urlProps.get('tab') }) }, t('prize_analysis'))),
            React.createElement("li", { className: gs('nav-item') },
                React.createElement(Link, { to: urlProps.mergeLink({ tab: 'won_prizes', won_prizes: null }), className: gs('nav-link', { active: urlProps.get('tab') == 'won_prizes' }) }, t('won_prizes')))),
        !urlProps.get('tab') && React.createElement(PrizeAnalysisTab, { drawResult: activeDrawResult }),
        urlProps.get('tab') == 'won_prizes' && React.createElement(WonPrizesTab, { drawResult: activeDrawResult }),
        React.createElement(ConfirmationModal, { modal: verifyModal },
            React.createElement("h5", { className: gs('my-2') }, t('confirm_verify_draw'))),
        React.createElement(ConfirmationModal, { modal: rejectModal },
            React.createElement("h5", { className: gs('my-2') }, t('confirm_reject_draw')))));
};
var WonPrizesTab = function (_a) {
    var drawResult = _a.drawResult;
    var t = useTranslation()[0];
    var dataframe = useDataframe(function (config) {
        return WonPrizeService.getWonPrizes(__assign(__assign({}, config), { params: __assign(__assign({}, config.params), { draw_result: drawResult.id }) }));
    }, {
        name: 'result.won_prizes',
        perPage: 5
    });
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: gs('w-50') },
            React.createElement(SearchInput, { dataframe: dataframe })),
        React.createElement("table", { className: gs('table-overview', 'mt-4', { loading: dataframe.isLoading() }) },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", { style: { width: '20%' } }, t('ticket')),
                    React.createElement("th", { style: { width: '35%' } }, t('customer')),
                    React.createElement("th", { style: { width: '12.5%' } }, t('prize')),
                    React.createElement("th", { style: { width: '12.5%' } }, t('value')),
                    React.createElement("th", { style: { width: '20%' } }, t('distributed')))),
            React.createElement("tbody", null,
                React.createElement(TableLoader, { dataframe: dataframe, columns: 5 }),
                dataframe.getData().map(function (wonPrize) { return React.createElement("tr", { key: wonPrize.id },
                    React.createElement("td", null,
                        React.createElement(Link, { to: "/tickets/".concat(wonPrize.participation.ticket.id) },
                            React.createElement(TicketNumber, { number: wonPrize.participation.ticket.number })),
                        !wonPrize.participation.active && (React.createElement("span", { className: gs('ml-1') },
                            React.createElement("small", { className: gs('text-danger') },
                                React.createElement(FontAwesomeIcon, { icon: "times" }))))),
                    React.createElement("td", null,
                        wonPrize.participation.ticket.customer.name,
                        ' ',
                        wonPrize.participation.ticket.customer.verified_at && React.createElement("small", { className: gs('text-success') },
                            React.createElement(FontAwesomeIcon, { icon: "check" })),
                        wonPrize.participation.ticket.customer.rsu && React.createElement("small", { className: gs('text-muted') },
                            ' ',
                            " / ",
                            ' ',
                            wonPrize.participation.ticket.customer.rsu.title),
                        wonPrize.participation.ticket.customer.external_id && React.createElement("small", { className: gs('text-muted') },
                            ' ',
                            " / ",
                            ' ',
                            React.createElement("code", null, wonPrize.participation.ticket.customer.external_id))),
                    React.createElement("td", null, wonPrize.prize.title),
                    React.createElement("td", null, formatMoney(wonPrize.amount)),
                    React.createElement("td", null, wonPrize.prize_distribution ? React.createElement(Link, { to: "/prize-distributors/".concat(wonPrize.prize_distribution.prize_distributor_id, "/prize-distributions/").concat(wonPrize.prize_distribution.id) },
                        React.createElement(DateTime, { date: wonPrize.prize_distribution.created_at })) : React.createElement("span", { className: gs('text-muted') }, t(wonPrize.prize.prize_distributor_id ? 'undistributed' : 'not_applicable_short')))); }))),
        dataframe.getPageCount() > 1 && React.createElement("div", { className: gs('mt-4') },
            React.createElement(Pager, { dataframe: dataframe }))));
};
var PrizeAnalysisTab = function (_a) {
    var drawResult = _a.drawResult;
    var t = useTranslation()[0];
    return (React.createElement(React.Fragment, null,
        React.createElement("table", { className: gs('table-overview', 'mt-4') },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", { style: { width: '40%' } }, t('prize')),
                    React.createElement("th", { style: { width: '20%' } }, t('value')),
                    React.createElement("th", { style: { width: '20%' } }, t('count')),
                    React.createElement("th", { style: { width: '20%' } }, t('total_value')))),
            React.createElement("tbody", null, drawResult.analysis.map(function (stats) { return React.createElement("tr", { key: stats['prize']['id'] },
                React.createElement("td", null,
                    React.createElement("strong", null, stats['prize']['title'])),
                React.createElement("td", null, formatMoney(stats['amount'])),
                React.createElement("td", null, formatNumber(stats['count'])),
                React.createElement("td", null, formatMoney(stats['total_amount']))); })))));
};
export default withRouter(Draw);
